import '@/assets/styles/components/dialog-modal.styles.scss';
import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { DialogModalProps } from './modal.types';
import clsx from 'clsx';
import ClassNames from 'classnames';

const ICONS = {
	warning: props => <i className={clsx(props.className, 'icons-danger-circle')} />,
	error: props => <i className={clsx(props.className, 'icons-close-circle')} />,
	success: props => <i className={clsx(props.className, 'icons-check-circle')} />,
	info: props => <i className={clsx('icons-info-circle', props.className)} />,
	danger: props => <i className={clsx('icons-danger', props.className)} />,
	waiting: props => <i className={clsx('icons-hourglass-line', props.className)} />,
	trash: props => <i className={clsx('icons-trash-bin-minimalistic', props.className)} />
} as const;

const ICON_PROPS = {
	size: 33,
	className: 'modal-icon'
} as const;

const DialogModal: React.FC<DialogModalProps> = ({
	isOpen,
	onClose,
	children,
	title,
	CustomIcon,
	variant = 'info',
	primaryButtonText,
	primaryButtonActive = true,
	mobileBottomSheet = false,
	secondaryButtonText,
	secondaryButtonIcon,
	secondaryButtonActive = true,
	primaryButtonIcon,
	onPrimaryClick,
	onSecondaryClick,
	showCloseIcon = true,
	className,
	shouldCloseOnOverlayClick = true,
	customXIcon,
	showHeader = true
}) => {
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener('keydown', handleEscape);
			document.body.style.overflow = 'hidden';
			document.documentElement.style.overflow = 'hidden';
			document.body.style.overscrollBehavior = 'none';
			document.documentElement.style.overscrollBehavior = 'none';

			return () => {
				document.removeEventListener('keydown', handleEscape);
				document.body.style.overflow = 'unset';
				document.documentElement.style.overflow = 'unset';
				document.body.style.overscrollBehavior = 'auto';
				document.documentElement.style.overscrollBehavior = 'auto';
			};
		}
	}, [isOpen, onClose]);

	const ModalIcon = useMemo(() => {
		const Icon = ICONS[variant as keyof typeof ICONS];
		return (
			<Icon
				{...ICON_PROPS}
				className={clsx(ICON_PROPS.className, `modal-icon--${variant}`)}
			/>
		);
	}, [variant]);

	const modalClasses = clsx('modal-container', `modal-variant--${variant}`);

	const buttonConfig = useMemo(
		() => ({
			secondary: {
				show: Boolean(secondaryButtonText),
				text: secondaryButtonText,
				icon: secondaryButtonIcon,
				active: secondaryButtonActive,
				onClick: onSecondaryClick || onClose,
				className: 'modal-button modal-button-secondary'
			},
			primary: {
				show: Boolean(primaryButtonText),
				text: primaryButtonText,
				icon: primaryButtonIcon,
				active: primaryButtonActive,
				onClick: onPrimaryClick,
				className: 'modal-button modal-button--primary'
			}
		}),
		[secondaryButtonText, primaryButtonText, secondaryButtonIcon, primaryButtonIcon, onSecondaryClick, onPrimaryClick, onClose]
	);

	const onClickModalOverlay = () => {
		if (shouldCloseOnOverlayClick) {
			onClose();
		}
	};

	if (!isOpen) return null;

	const modalContent = (
		<div
			className={clsx('dialog-modal-overlay', className, {
				'mobile-bottom-sheet': mobileBottomSheet
			})}
			onClick={onClickModalOverlay}>
			<div
				className={modalClasses}
				onClick={e => e.stopPropagation()}>
				{showCloseIcon && (
					<button
						type="button"
						className="modal-close"
						onClick={onClose}
						aria-label="Close modal">
						{customXIcon ? (
							customXIcon
						) : (
							<X
								size={20}
								color="var(--text-primary)"
							/>
						)}
					</button>
				)}
				{showHeader && (
					<div className="modal-header">
						{CustomIcon ? CustomIcon : ModalIcon}
						{title && <h2 className={ClassNames('modal-title', variant && `${variant}`)}>{title}</h2>}
					</div>
				)}

				<div className="modal-content">{children}</div>

				{(buttonConfig.primary.show || buttonConfig.secondary.show) && (
					<div className="modal-footer">
						{buttonConfig.secondary.show && (
							<button
								type="button"
								className={clsx(buttonConfig.secondary.className, variant && `modal-button-secondary--${variant}`)}
								disabled={!buttonConfig.secondary.active}
								onClick={buttonConfig.secondary.onClick}>
								{buttonConfig.secondary.icon && <span className="button-icon button-icon-secondary">{buttonConfig.secondary.icon}</span>}
								{buttonConfig.secondary.text}
							</button>
						)}
						{buttonConfig.primary.show && (
							<button
								type="button"
								className={buttonConfig.primary.className}
								disabled={!buttonConfig.primary.active}
								onClick={buttonConfig.primary.onClick}>
								{buttonConfig.primary.icon && <span className="button-icon button-icon-primary">{buttonConfig.primary.icon}</span>}
								{buttonConfig.primary.text}
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);

	return createPortal(modalContent, document.body);
};

export default DialogModal;
